import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { eur } from "@/components/LineItemsEditor";
import { Button } from "@/components/ui/button";
import { Wallet, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const METHOD_LABEL = { espece: "Espèces", cb: "Carte bancaire", virement: "Virement", cheque: "Chèque", autre: "Autre" };

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");

  useEffect(() => { api.get("/payments").then((r) => setPayments(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false)); }, []);

  const now = new Date();
  const filtered = payments.filter((p) => {
    if (period === "all") return true;
    const d = p.date || "";
    if (period === "month") return d.startsWith(now.toISOString().slice(0, 7));
    if (period === "year") return d.startsWith(String(now.getFullYear()));
    if (period === "quarter") { const m = parseInt(d.slice(5, 7), 10); const q = Math.floor((now.getMonth()) / 3); return d.startsWith(String(now.getFullYear())) && Math.floor((m - 1) / 3) === q; }
    return true;
  });
  const total = filtered.reduce((s, p) => s + (p.amount || 0), 0);

  const exportCSV = () => {
    const rows = [["Date", "Facture", "Client", "Mode", "Montant"]].concat(filtered.map((p) => [p.date, p.invoice_number || "", p.client_name || "", METHOD_LABEL[p.method] || p.method, String(p.amount)]));
    const csv = rows.map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "livre-recettes.csv"; a.click();
  };

  return (
    <div>
      <PageHeader title="Encaissements" subtitle="Livre des recettes — généré à partir des règlements" icon={Wallet}
        actions={<Button variant="outline" onClick={exportCSV} data-testid="btn-export-csv" className="border-slate-200 bg-white"><Download className="w-4 h-4 mr-1.5" /> Export CSV</Button>} />

      <div className="flex items-center gap-2 mb-4">
        {[["all", "Tout"], ["month", "Ce mois"], ["quarter", "Ce trimestre"], ["year", "Cette année"]].map(([k, l]) => (
          <button key={k} onClick={() => setPeriod(k)} data-testid={`period-${k}`} className={`px-3 py-1.5 rounded-lg text-sm border ${period === k ? "bg-emerald-500 text-black border-emerald-500 font-semibold" : "bg-white border-slate-200 text-slate-500"}`}>{l}</button>
        ))}
        <div className="ml-auto text-sm text-slate-500">Total encaissé : <span className="font-mono font-semibold text-emerald-600" data-testid="payments-total">{eur(total)}</span></div>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
        : filtered.length === 0 ? <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">Aucun encaissement sur cette période.</div>
        : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <tr><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Facture</th><th className="text-left px-4 py-3">Client</th><th className="text-left px-4 py-3">Mode</th><th className="text-right px-4 py-3">Montant</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filtered.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50" data-testid={`payment-row-${p.id}`}>
                      <td className="px-4 py-3 text-slate-500">{p.date}</td>
                      <td className="px-4 py-3 font-mono text-slate-900">{p.invoice_number}</td>
                      <td className="px-4 py-3 text-slate-700">{p.client_name}</td>
                      <td className="px-4 py-3 text-slate-700">{METHOD_LABEL[p.method] || p.method}</td>
                      <td className="px-4 py-3 text-right font-mono text-emerald-600">{eur(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
    </div>
  );
}
